import { useMachine } from '@xstate/react';
import { add, Interval, isAfter, isBefore, parse, sub, toDate } from 'date-fns';
import * as R from 'ramda';
import React, { useEffect, useState } from 'react';
import { DayPicker, DayPickerProps, Labels } from 'react-day-picker';
import { AnyState } from 'xstate';
import { Day } from './Day';
import { OnChangeProps, PickerProps, StateTarget } from './models';
import { convertToMatcherArray, createDateRangeMachine, createWeekNumberComponent, isStartOfDay } from './utils';

interface HoverState {
  target: Element | null;
  date: Date | null;
  datetime: string | null;
}

interface DateRangePickerProps extends PickerProps {
  initialRange?: Interval;
  labels?: Partial<Labels>;
  children?: (props: {
    send: (type: string, data: object) => void;
    state: AnyState;
    range: Interval;
  }) => React.JSX.Element;
  onChange?: (props: OnChangeProps) => void;
}

const getSelectedRange = (state: AnyState): Interval => {
  if (R.propEq('value', StateTarget.OneDateSelected)(state)) {
    return {
      start: state.context.range.start,
      end: state.context.range.start,
    };
  }

  if (
    R.allPass([R.propEq('value', StateTarget.TwoDatesSelected), (s: AnyState) => isStartOfDay(s.context.range.end)])(
      state,
    )
  ) {
    return {
      start: state.context.range.start,
      // NOTE: The start of the next day is allowed for the range's `end`
      // indicating that the range ends 'midnight'. We don't want that for the
      // `DayPicker` component.
      end: sub(state.context.range.end, { days: 1 }),
    };
  }

  return state.context.range;
};

export const DateRangePicker: React.FC<DateRangePickerProps> = ({
  locale,
  startOfWeek,
  children,
  disabled,
  initialRange,
  rangeMaxDays,
  onChange = (): void => undefined,
  labels,
  ...props
}) => {
  const [state, send, service] = useMachine(() =>
    createDateRangeMachine(initialRange ?? { start: new Date(), end: new Date() }, rangeMaxDays),
  );

  useEffect(() => {
    const subscription = service.subscribe((nextState) => {
      onChange({ range: nextState.context.range, state: nextState });
    });

    return subscription.unsubscribe;
  }, [service, onChange]);

  const selectedRange = getSelectedRange(state);

  // Track which element is hovered
  const [hoverState, setHoverState] = useState<HoverState>({
    target: null,
    datetime: null,
    date: null,
  });

  const disabledArray = convertToMatcherArray(disabled);

  const getModifierProps = (modState: AnyState): object => {
    if (modState.value === StateTarget.OneDateSelected) {
      return {
        disabled: [
          ...disabledArray,
          state.context.limit.start && { before: state.context.limit.start },
          state.context.limit.end && { after: state.context.limit.end },
        ].filter(Boolean),
        hovered: [
          {
            after: hoverState.date ? R.min(hoverState.date, selectedRange.start) : selectedRange.start,
            before: hoverState.date
              ? add(R.max(hoverState.date, selectedRange.start), { days: 1 })
              : add(selectedRange.start, { days: 1 }),
          },
        ],
        'range-start':
          hoverState.date && hoverState.date !== selectedRange.start
            ? R.min(hoverState.date, selectedRange.start)
            : selectedRange.start,
        'range-end':
          hoverState.date && hoverState.date !== selectedRange.start
            ? R.max(hoverState.date, selectedRange.start)
            : selectedRange.start,
        'in-the-middle':
          hoverState.date && hoverState.date.getTime() !== toDate(selectedRange.start).getTime()
            ? [
                {
                  after: R.min(hoverState.date, selectedRange.start),
                  before: R.max(hoverState.date, selectedRange.start),
                },
              ]
            : undefined,
      };
    }

    if (modState.value === StateTarget.TwoDatesSelected || modState.value === StateTarget.WeekSelected) {
      return {
        'range-start': selectedRange.start,
        'range-end': selectedRange.end,
        'in-the-middle': [
          {
            after: selectedRange.start,
            before: selectedRange.end,
          },
        ],
      };
    }

    return {};
  };

  const dayPickerProps = {
    mode: undefined,
    components: {
      Day,
      WeekNumber: createWeekNumberComponent(disabledArray),
    },
    disabled,
    locale,
    modifiers: getModifierProps(state),
    modifiersClassNames: {
      hovered: 'hovered',
      'range-start': 'range-start',
      'range-end': 'range-end',
      'in-the-middle': 'in-the-middle',
    },
    onDayClick: (day: Date) => {
      send('click:day', { date: day });
    },
    onWeekNumberClick: (weekNumber: number, dates: Date[]) => {
      // Find first non-disabled (past) date in the week
      const range = disabledArray.reduce(
        (prev, rule) => {
          if (!rule || typeof rule !== 'object') {
            return prev;
          }

          let result = prev;
          if ('before' in rule) {
            result = {
              ...result,
              start: dates.find((date) => !isBefore(date, rule.before)) ?? result.start,
            };
          }

          if ('after' in rule) {
            result = {
              ...result,
              end: [...dates].reverse().find((date) => !isAfter(date, rule.after)) ?? result.end,
            };
          }

          return result;
        },
        { start: dates[0], end: dates[dates.length - 1] },
      );

      send('click:week-number', { range });
    },
    selected: {
      from: selectedRange.start as Date,
      to: selectedRange.end as Date,
    },
    weekStartsOn: startOfWeek,
    labels,
    ...props,
  } as DayPickerProps;

  const hoverProps = {
    onMouseOver: (event: React.MouseEvent) => {
      const target = (event.target as Element).closest('.rdp-cell');
      const isDisabled = target?.querySelector('button[disabled]');

      if (isDisabled) {
        setHoverState((previous) => {
          return {
            ...previous,
            target: null,
            datetime: null,
            date: null,
          };
        });
      } else if (target !== hoverState.target) {
        setHoverState((previous) => {
          const datetime = target?.querySelector('[datetime]')?.getAttribute('datetime') ?? null;
          const date = datetime ? parse(datetime, 'yyyy-MM-dd', new Date()) : null;

          return {
            ...previous,
            target,
            datetime,
            date,
          };
        });
      }
    },
  };

  return (
    <section className="date-picker fex flex-col" {...hoverProps}>
      <header />
      <DayPicker {...dayPickerProps} />
      {children && <div className="p-2">{children({ send, state, range: state.context.range })}</div>}
    </section>
  );
};
